import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { EURO } from "./FinanceFilter";
import { InputNumericRange } from "components";

interface IValue {
  value: any;
  name: string;
  changeValue: any;
}
interface ISlider extends IValue {
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
}

export const SideFilter: React.FC<ISlider> = ({
  name,
  changeValue,
  max,
  min,
  step = 1000,
  value,
  disabled = false,
}) => {
  const submit = (newValue: any) => {
    changeValue(name, newValue);
  };
  return (
    <>
      <Typography variant="h6">
        <FormattedMessage id="filter.financial.monthlyRate.title.label" />
      </Typography>
      <InputNumericRange
        onChangeHandler={submit}
        max={max}
        min={min}
        step={step}
        value={value}
        unit="€"
      />
    </>
  );
};

const DropdownFilter: React.FC<ISlider> = ({
  name,
  changeValue,
  max,
  min,
  step = 5000,
  value,
}) => {
  const { formatMessage: t } = useIntl();

  const setValueHandler = (event: SelectChangeEvent) => {
    const newValue = event.target.value;
    if (newValue === "all") changeValue("rate", [min, max]);
    else {
      const numericValue =
        typeof newValue === "number"
          ? newValue
          : parseInt(String(newValue), 10);
      changeValue("rate", [min, numericValue]);
    }
  };

  const options = [];
  for (var i = min; i <= max; i += step) {
    options.push(i);
  }

  return (
    <FormControl component="fieldset" size="small">
      <InputLabel id="filter-finance-rate-label">
        <FormattedMessage
          id="filter.finance.rate.label"
          defaultMessage="Rate bis"
        />
      </InputLabel>
      <Select
        fullWidth
        labelId="filter-finance-rate-label"
        id="filter-finance-rate-simple"
        value={value[0] + value[1] === min + max ? "all" : value[1]}
        label={t({
          id: "filter.finance.rate.label",
          defaultMessage: "Rate bis",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value="all">Alle</MenuItem>
        {options.map((i) => (
          <MenuItem value={i}>
            <EURO value={i} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const DropdownInputFilter: React.FC<ISlider> = ({
  name,
  changeValue,
  max,
  min,
  step = 5000,
  value,
}) => {
  const { formatMessage: t } = useIntl();
  const [inputValue, setInputValue] = React.useState("");

  const handleChange = (event: any, newValue: string | number | null) => {
    if (newValue === "all" || newValue === null || newValue === "")
      changeValue("rate", [min, max]);
    else changeValue("rate", [min, newValue]);
  };

  const options = [];
  for (var i = min; i <= max; i += step) {
    options.push(i.toString());
  }

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={value !== null ? String(value) : ""}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        if (/^\d*$/.test(newInputValue)) {
          setInputValue(newInputValue);
        }
      }}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          onBlur={() => {
            handleChange(null, inputValue);
          }}
          label={t({
            id: "filter.finance.rate.label",
            defaultMessage: "Rate bis",
          })}
        />
      )}
    />
  );
};

interface IContainer extends ISlider {
  variant?: string;
}

const Container: React.FC<IContainer> = ({ variant = "slider", ...rest }) => {
  let Filter;
  switch (variant) {
    case "dropdown":
      Filter = DropdownFilter;
      break;
    case "dropdown+input":
      Filter = DropdownInputFilter;
      break;
    case "slider":
      Filter = SideFilter;
      break;
    default:
      Filter = SideFilter;
  }
  return <Filter {...rest} />;
};

export default Container;
